/**
 * Translation keys (register in backend / CMS):
 * - sync.credentials, sync.credentials.new, sync.credentials.edit
 * - sync.credentials.empty, sync.credentials.empty.filter
 * - sync.credentials.filter.includeexpired
 * - sync.credentials.col.integration, .clientid, .fingerprint, .expiry
 * - sync.credentials.form.syncstep, sync.credentials.form.syncstep.select
 * - sync.credentials.form.expires, sync.credentials.form.expiry, sync.credentials.form.secret.placeholder
 * - sync.credentials.form.recipients, sync.credentials.form.recipients.desc
 * - sync.credentials.form.recipients.placeholder, sync.credentials.form.recipients.add
 * - sync.credentials.btn.create
 * - sync.credentials.message.created, sync.credentials.message.updated
 */
import * as ko from "knockout";
import PromiseRequest from "../../../req/PromiseRequest";
import {ObjectTypeId} from "../../../constants";
import {PLEASE_WAIT_MESSAGE} from "../../../constants/Language";
import {ICompanySyncstep, IKnockoutComponent, IMessageData, IObjectData, ISearchObjectParams, ISyncField} from "../../../index";
import {createErrorMessage, credentialMatchesProfile, getRawSyncFieldValue} from "../../../util/misc";
import {ISplitButtonParams} from "@component/button/splitButton/split-button";
import {getSearchObjectParams} from "../../../util/request";
import {global, lm} from "../../../appContext";
import pubsub from "../../../pubsub";
import {nsClone} from "../../../clone/fn";
import {convertFromServerString, convertToServerString} from "../../../util/datetime";
import {moment} from 'moment';
import {CredentialFieldVm, IClientCredentialField} from "./CredentialFieldVm";
import detailsHtml from './details.html?raw';

export const template = detailsHtml;

type TCrudMode = 'list' | 'create' | 'edit';

interface ICredentialRow {
    object: IObjectData
    id: number
    name: string
    /** Stored ExtensionAttribute5 (credential type on the object). */
    integrationCode: string
    /** Authoritative integration code from getclientcredentialprofile for the matching sync step (e.g. ImportHR:14). */
    integrationProfileCode: string
    clientIdOrUsername: string
    fingerprint: string
    expiryDate: string
}

interface IClientCredentialProfile {
    integrationcode: string
    fields: IClientCredentialField[]
}

interface ISyncstepOption {
    id: number
    name: string
}

export class viewModel {
    readonly credentialFormWaitMarkup = PLEASE_WAIT_MESSAGE;
    readonly messageData = ko.observable<IMessageData | null>(null);
    readonly isLoading = ko.observable(true);
    readonly isSubmitting = ko.observable(false);
    readonly isCredentialFormInitializing = ko.observable(false);
    readonly rows = ko.observableArray<ICredentialRow>([]);
    readonly listSearchQuery = ko.observable('');
    readonly includeExpired = ko.observable(false);
    readonly filteredCredentialRows = ko.pureComputed(() => {
        const q = this.listSearchQuery().trim().toLowerCase();
        const includeExpired = this.includeExpired();
        return this.rows().filter((row) => {
            if (!includeExpired && this.isExpiredCredential(row.expiryDate)) {
                return false;
            }
            if (!q) {
                return true;
            }
            return [row.name, row.integrationProfileCode, row.integrationCode, row.clientIdOrUsername, row.fingerprint]
                .some((v) => (v ?? '').toLowerCase().includes(q));
        });
    });

    /** Heading badge: full visible count when idle, hit count when text-searching. */
    readonly searchBadge = ko.pureComputed((): number | null => {
        if (this.isLoading()) {
            return null;
        }
        return this.filteredCredentialRows().length;
    });

    /** Spinner on first load only (client-side filter is instant thereafter). */
    readonly searchBadgeWaiting = ko.pureComputed((): boolean => this.isLoading());
    readonly mode = ko.observable<TCrudMode>('list');
    readonly syncstepOptions = ko.observableArray<ISyncstepOption>([]);
    /** Subset of syncstepOptions that returned a non-empty credential profile — used for the create/edit form dropdown. */
    readonly credentialCapableSyncstepOptions = ko.pureComputed<ISyncstepOption[]>(() => {
        void this.profileCacheVersion(); // re-evaluate when any profile loads
        return this.syncstepOptions().filter((o) => (this.profileCacheBySyncstepId[o.id]?.fields?.length ?? 0) > 0);
    });
    readonly selectedSyncstepId = ko.observable<number | null>(null);
    readonly profile = ko.observable<IClientCredentialProfile>(null);
    readonly credentialName = ko.observable('');
    readonly credentialDescription = ko.observable('');
    readonly credentialExpiryDate = ko.observable('');
    readonly credentialExpires = ko.observable(false);
    readonly credentialEmails = ko.observableArray<string>([]);
    readonly newEmailInput = ko.observable('');
    readonly dynamicFields = ko.observableArray<CredentialFieldVm>([]);

    private readonly reqSearchObject = new PromiseRequest('searchobject');
    private readonly reqCreateObject = new PromiseRequest('createobject');
    private readonly reqUpdateObject = new PromiseRequest('updateobject');
    private readonly reqGetObject = new PromiseRequest('getobject');
    private readonly reqGetCompanySyncsteps = new PromiseRequest('getcompanysyncsteps');
    private readonly reqGetClientCredentialProfile = new PromiseRequest('getclientcredentialprofile');
    private readonly profileCacheBySyncstepId: Record<number, IClientCredentialProfile> = {};
    private readonly profileCacheVersion = ko.observable(0);
    private currentEditObject: IObjectData | null = null;

    constructor() {
        this.selectedSyncstepId.subscribe((syncstepId: number | null) => {
            if (syncstepId != null) {
                this.onSyncstepChanged(syncstepId);
            }
        });
        pubsub.on(this, "clientcredential.delete.success", this.onCredentialDeleted);
        this.init();
    }

    private readonly onCredentialDeleted = () => {
        void this.loadCredentials();
    };

    private init() {
        this.isLoading(true);
        Promise.resolve()
            .then(() => this.loadSyncsteps())
            .then(() => this.loadCredentials())
            .catch((reason: string) => {
                this.messageData(createErrorMessage(String(reason)));
            })
            .finally(() => {
                this.isLoading(false);
            });
    }

    private loadCredentials() {
        this.isLoading(true);
        this.rows([]);
        if (!this.isSubmitting()) {
            this.messageData({message: PLEASE_WAIT_MESSAGE});
        }
        this.reqSearchObject.abort();

        const params: ISearchObjectParams = getSearchObjectParams({
            objecttypeid: ObjectTypeId.ClientCredential,
            manualobjects: true,
            searchcols: 'Name,ExtensionAttribute5,ExtensionAttribute1,ExtensionAttribute4,ExpiryDate,Description',
            sortcol: 'Name',
            deleted: 0,
            resultsprpage: 1000,
            pagenr: 0,
        });

        return this.reqSearchObject
            .request(params)
            .then(async (data: IObjectData[]) => {
                const rows = data.map(this.mapToCredentialRow);
                this.rows(await this.attachProfileIntegrationCodesToRows(rows));
                this.messageData(null);
            })
            .catch((reason: string) => {
                this.messageData(createErrorMessage(String(reason)));
            })
            .finally(() => {
                this.isLoading(false);
            });
    }

    private loadSyncsteps() {
        this.reqGetCompanySyncsteps.abort();
        return this.reqGetCompanySyncsteps
            .request()
            .then((data: ICompanySyncstep[]) => {
                const mapped = data
                    .sort((a, b) => a.order - b.order)
                    .map((o) => ({id: o.syncstep.id, name: o.syncstep.name}));
                const unique = mapped.filter((option, index, arr) => {
                    return arr.findIndex((inner) => inner.id === option.id) === index;
                });
                this.syncstepOptions(unique);
                if (!this.selectedSyncstepId() && unique.length) {
                    const first = unique[0];
                    if (first) {
                        this.selectedSyncstepId(first.id);
                    }
                }
            });
    }

    private async onSyncstepChanged(syncstepId: number) {
        if (!syncstepId) {
            this.profile(null);
            this.dynamicFields([]);
            return;
        }
        try {
            const profile = await this.getProfile(syncstepId);
            this.profile(profile);
            this.disposeCredentialFieldComputeds(this.dynamicFields());
            this.dynamicFields(profile.fields.map((field) => new CredentialFieldVm(field)));
            if (this.mode() === 'edit' && this.currentEditObject) {
                this.populateDynamicFieldsFromObject(this.currentEditObject, true);
            }
        } catch (reason: unknown) {
            this.profile(null);
            this.dynamicFields([]);
            this.messageData(createErrorMessage(String(reason)));
        }
    }

    private getProfile(syncstepId: number): Promise<IClientCredentialProfile> {
        if (this.profileCacheBySyncstepId[syncstepId]) {
            return Promise.resolve(this.profileCacheBySyncstepId[syncstepId]);
        }
        this.reqGetClientCredentialProfile.abort();
        return this.reqGetClientCredentialProfile
            .request({syncstepid: syncstepId})
            .then((data: IClientCredentialProfile | IClientCredentialProfile[]) => {
                const profile = Array.isArray(data) ? data[0] : data;
                if (!profile) {
                    throw new Error(`Missing credential profile for syncstepid=${syncstepId}`);
                }
                this.profileCacheBySyncstepId[syncstepId] = profile;
                this.profileCacheVersion(this.profileCacheVersion() + 1);
                return profile;
            });
    }

    private isExpiredCredential(expiryDate: string): boolean {
        if (!expiryDate?.trim()) {
            return false;
        }
        const parsed = convertFromServerString(expiryDate);
        if (!parsed?.isValid()) {
            return false;
        }
        return parsed.isBefore(moment());
    }

    private mapToCredentialRow = (obj: IObjectData): ICredentialRow => {
        return {
            object: obj,
            id: obj.id,
            name: getRawSyncFieldValue('Name', obj.syncfields) ?? '',
            integrationCode: getRawSyncFieldValue('ExtensionAttribute5', obj.syncfields) ?? '',
            integrationProfileCode: '',
            clientIdOrUsername: getRawSyncFieldValue('ExtensionAttribute1', obj.syncfields) ?? '',
            fingerprint: getRawSyncFieldValue('ExtensionAttribute4', obj.syncfields) ?? '',
            expiryDate: getRawSyncFieldValue('ExpiryDate', obj.syncfields) ?? '',
        };
    };

    /**
     * Loads profiles for all company sync steps (sequential — getProfile shares one PromiseRequest),
     * then sets integrationProfileCode to the matching profile's integrationcode (not raw ExtensionAttribute5).
     */
    private async attachProfileIntegrationCodesToRows(rows: ICredentialRow[]): Promise<ICredentialRow[]> {
        const options = this.syncstepOptions();
        if (!options.length) {
            return rows.map((row) => ({
                ...row,
                integrationProfileCode: (row.integrationCode ?? '').trim(),
            }));
        }
        for (const option of options) {
            await this.getProfile(option.id);
        }
        return rows.map((row) => ({
            ...row,
            integrationProfileCode: this.resolveProfileIntegrationCodeForStored(row.integrationCode),
        }));
    }

    private resolveProfileIntegrationCodeForStored(storedExtensionAttribute5: string): string {
        const stored = (storedExtensionAttribute5 ?? '').trim();
        for (const option of this.syncstepOptions()) {
            const profile = this.profileCacheBySyncstepId[option.id];
            const profileCode = profile?.integrationcode ?? '';
            if (credentialMatchesProfile(stored, profileCode)) {
                return profileCode.trim();
            }
        }
        return stored;
    }

    private getSyncFieldValuesMap(syncfields: ISyncField[]): Record<string, string> {
        const map: Record<string, string> = {};
        syncfields?.forEach((sf) => {
            map[sf.name] = sf.value;
        });
        return map;
    }

    private setModeList() {
        this.mode('list');
        this.isCredentialFormInitializing(false);
        this.currentEditObject = null;
        this.resetForm();
    }

    private disposeCredentialFieldComputeds(fields: CredentialFieldVm[]) {
        fields.forEach((f) => f.effectiveNonMaskInputType.dispose());
    }

    readonly addEmail = () => {
        const email = this.newEmailInput().trim();
        if (email) {
            this.credentialEmails.push(email);
            this.newEmailInput('');
        }
    };

    readonly removeEmail = (email: string) => {
        this.credentialEmails.remove(email);
    };

    private resetForm() {
        this.credentialName('');
        this.credentialDescription('');
        this.credentialExpires(false);
        this.credentialExpiryDate('');
        this.credentialEmails([]);
        this.newEmailInput('');
        this.profile(null);
        this.disposeCredentialFieldComputeds(this.dynamicFields());
        this.dynamicFields([]);
    }

    private prepareCreateMode = async () => {
        this.messageData(null);
        this.isCredentialFormInitializing(true);
        this.currentEditObject = null;
        this.mode('create');
        this.resetForm();
        try {
            const firstSyncstepId = this.credentialCapableSyncstepOptions()[0]?.id ?? null;
            this.selectedSyncstepId(firstSyncstepId);
            if (firstSyncstepId) {
                await this.onSyncstepChanged(firstSyncstepId);
            }
        } finally {
            this.isCredentialFormInitializing(false);
        }
    };

    private prepareEditMode = async (row: ICredentialRow) => {
        this.messageData(null);
        this.isCredentialFormInitializing(true);
        this.mode('edit');
        this.reqGetObject.abort();
        return this.reqGetObject
            .request({id: row.id, manual: true})
            .then(async (data: IObjectData[]) => {
                const obj = data[0];
                if (!obj) {
                    throw new Error(`Missing credential object for id=${row.id}`);
                }
                this.currentEditObject = obj;
                const syncMap = this.getSyncFieldValuesMap(obj.syncfields);
                this.credentialName(syncMap['Name'] ?? '');
                this.credentialDescription(syncMap['Description'] ?? '');
                const existingExpiry = this.toDateInputValue(syncMap['ExpiryDate'] ?? '');
                this.credentialExpires(!!existingExpiry);
                this.credentialExpiryDate(existingExpiry);
                this.credentialEmails(
                    (syncMap['ExtensionAttribute6'] ?? '').split(',').map((e) => e.trim()).filter(Boolean),
                );

                const integrationCode = syncMap['ExtensionAttribute5'] ?? '';
                const selectedSyncstepId = await this.findSyncstepIdByIntegrationCode(integrationCode);
                this.selectedSyncstepId(selectedSyncstepId ?? this.syncstepOptions()[0]?.id ?? null);

                if (this.selectedSyncstepId() != null) {
                    await this.onSyncstepChanged(this.selectedSyncstepId()!);
                }
                this.populateDynamicFieldsFromObject(obj, true);
                this.messageData(null);
            })
            .catch((reason: string) => {
                this.messageData(createErrorMessage(String(reason)));
                this.setModeList();
            })
            .finally(() => {
                this.isCredentialFormInitializing(false);
            });
    };

    private populateDynamicFieldsFromObject(obj: IObjectData, isEditMode = false) {
        const syncMap = this.getSyncFieldValuesMap(obj.syncfields);
        const fingerprint = syncMap['ExtensionAttribute4'] ?? '';
        this.dynamicFields().forEach((dynamicField) => {
            const key = dynamicField.field.name;
            if (isEditMode && key === 'ExtensionAttribute3') {
                dynamicField.beginWithFingerprint(fingerprint);
                return;
            }
            dynamicField.clearSecretMask();
            dynamicField.value(syncMap[key] ?? '');
        });
    }

    private async findSyncstepIdByIntegrationCode(integrationCode: string): Promise<number | null> {
        if (!integrationCode) {
            return null;
        }
        for (const option of this.syncstepOptions()) {
            const profile = await this.getProfile(option.id);
            const profileCode = profile?.integrationcode ?? '';
            if (credentialMatchesProfile(integrationCode, profileCode)) {
                return option.id;
            }
        }
        return null;
    }

    private onCancelClick = () => {
        this.setModeList();
        this.messageData(null);
    };

    private isFieldRequired(fieldVm: CredentialFieldVm): boolean {
        if (this.mode() === 'edit' && fieldVm.field.name === 'ExtensionAttribute3') {
            return false;
        }
        return !!fieldVm.field.required;
    }

    getFieldPlaceholder(fieldVm: CredentialFieldVm): string {
        if (fieldVm.field.name !== 'ExtensionAttribute3') {
            return '';
        }
        if (this.mode() === 'edit' && fieldVm.useSecretFingerprintMask()) {
            return '';
        }
        if (this.mode() === 'edit') {
            return lm.t('sync.credentials.form.secret.placeholder') ?? '';
        }
        return '';
    }

    get syncstepSelectCaption(): string {
        return lm.t('sync.credentials.form.syncstep.select') ?? '';
    }

    getListHeaderSplitButtonParams = (): ISplitButtonParams | null => {
        if (this.mode() !== 'list') {
            return null;
        }
        return {
            mainButton: {
                cls: 'btn-action btn-sm',
                icon: 'fa-plus',
                label: 'sync.credentials.btn.create',
                onClick: () => {
                    void this.prepareCreateMode();
                },
                validator: () => !this.isSubmitting(),
            },
        };
    };

    getCredentialRowActionsComponent = (row: ICredentialRow): IKnockoutComponent => ({
        name: 'component/button/ellipsis-dropdown',
        params: {
            buttons: [
                {
                    label: 'edit',
                    onClick: () => {
                        void this.prepareEditMode(row);
                    },
                    validator: () => !this.isSubmitting(),
                },
                {
                    label: 'delete',
                    onClick: () => this.openDeleteCredentialDialog(row),
                    cls: 'btn-danger btn-ready-calm',
                    validator: () => !this.isSubmitting(),
                },
            ],
        },
    });

    private canSubmit = ko.pureComputed(() => {
        if (this.mode() === 'list') {
            return false;
        }
        if (!this.selectedSyncstepId() || !this.profile()) {
            return false;
        }
        if (this.mode() === 'create' && !(this.profile()?.integrationcode ?? '').trim()) {
            return false;
        }
        if (!this.credentialName().trim()) {
            return false;
        }
        return this.dynamicFields().every((fieldVm) => {
            if (!this.isFieldRequired(fieldVm)) {
                return true;
            }
            return !!fieldVm.value()?.trim();
        });
    });

    private onSubmitClick = () => {
        if (!this.canSubmit()) {
            return;
        }
        this.isSubmitting(true);
        this.messageData({message: PLEASE_WAIT_MESSAGE});
        const payload = this.getSubmitObject();
        const request = this.mode() === 'create'
            ? this.reqCreateObject.request({jobject: JSON.stringify(payload)})
            : this.reqUpdateObject.request({jobject: JSON.stringify(payload)});

        request
            .then(() => {
                global.cacheManager.removeCache('searchobject');
                global.cacheManager.removeCache('getobject');
                return this.loadCredentials();
            })
            .then(() => {
                const messageKey = this.mode() === 'create'
                    ? 'sync.credentials.message.created'
                    : 'sync.credentials.message.updated';
                global.snackbarData({message: messageKey});
                this.setModeList();
                this.messageData(null);
            })
            .catch((reason: string) => {
                this.messageData(createErrorMessage(String(reason)));
            })
            .finally(() => {
                this.isSubmitting(false);
            });
    };

    private getSubmitObject(): Partial<IObjectData> {
        const orgnr = global.login.selectedOrganizationId() ?? '';
        const profile = this.profile();
        if (!profile) {
            return {};
        }
        const syncfields: Array<{ name: string, value: string | null }> = [
            {name: 'Name', value: this.credentialName().trim()},
            {name: 'Description', value: this.credentialDescription().trim() || null},
            {name: 'ExpiryDate', value: this.credentialExpires() ? this.toServerDateString(this.credentialExpiryDate()) : null},
            {name: 'ExtensionAttribute6', value: this.credentialExpires()
                ? this.credentialEmails().filter(Boolean).join(',') || null
                : null},
        ];
        if (this.mode() === 'create') {
            syncfields.push({name: 'ExtensionAttribute5', value: (profile.integrationcode ?? '').trim() || null});
        }

        this.dynamicFields().forEach((fieldVm) => {
            if (fieldVm.field.name === 'ExtensionAttribute5') {
                return;
            }
            const value = fieldVm.value()?.trim() ?? '';
            if (this.mode() === 'edit' && fieldVm.field.name === 'ExtensionAttribute3' && !value) {
                return;
            }
            syncfields.push({name: fieldVm.field.name, value: value || null});
        });

        const payload: Partial<IObjectData> = {
            companyid: orgnr,
            orgnr: orgnr,
            manual: true,
            type: {id: ObjectTypeId.ClientCredential, name: 'ClientCredential'},
            syncfields: syncfields as ISyncField[],
        };
        if (this.mode() === 'edit' && this.currentEditObject) {
            payload.id = this.currentEditObject.id;
        }
        return payload;
    }

    private toServerDateString(dateInput: string): string | null {
        if (!dateInput) {
            return null;
        }
        const parsed = moment(dateInput, 'YYYY-MM-DD', true);
        if (!parsed.isValid()) {
            return null;
        }
        return convertToServerString(parsed);
    }

    private toDateInputValue(serverDate: string): string {
        if (!serverDate) {
            return '';
        }
        const parsed = convertFromServerString(serverDate);
        if (!parsed?.isValid()) {
            return '';
        }
        return parsed.format('YYYY-MM-DD');
    }

    private openDeleteCredentialDialog = (row: ICredentialRow) => {
        nsClone.dialogDelete(
            {
                icon: "fa-key",
                translationKey: "clientcredential",
            },
            "component/sync/credentials/dialog/delete",
            {id: row.id, name: (row.name && row.name.trim()) || `#${row.id}`},
        );
    };

    dispose() {
        pubsub.off(this);
        this.filteredCredentialRows.dispose();
        this.disposeCredentialFieldComputeds(this.dynamicFields());
        this.reqSearchObject.abort();
        this.reqCreateObject.abort();
        this.reqUpdateObject.abort();
        this.reqGetObject.abort();
        this.reqGetCompanySyncsteps.abort();
        this.reqGetClientCredentialProfile.abort();
    }
}
